/**
 * 多值单元格拆分：逗号/分号为分隔符；顿号「、」仅在「已知标签」保护下拆分，
 * 避免把「钢铁、金属冶炼类」等选项名误切。
 */

export type SplitMultiValueOptions = {
  /** 含顿号的合法整词（如标准产业枚举），按长度优先匹配 */
  knownLabels?: readonly string[];
  /** 别名 → 规范名（如「农副食品加工、食品制造」→「农副食品加工、食品」） */
  aliases?: Readonly<Record<string, string>>;
};

const DEFAULT_SEP = /[,，;；]/;
const LEADING_SEP_CHARS = ',，;；、';

function isSepChar(c: string | undefined): boolean {
  return c != null && LEADING_SEP_CHARS.includes(c);
}

/**
 * 通用多值拆分（不含顿号）。
 */
export function splitMultiValueCell(raw: unknown): string[] {
  if (raw == null || raw === '') return [];
  const s = String(raw).trim();
  if (!s) return [];
  return s
    .split(DEFAULT_SEP)
    .map((x) => x.trim())
    .filter(Boolean);
}

/**
 * 主导产业等：在已知标签保护下，将顿号也视为项间分隔。
 */
export function splitLabeledMultiValue(
  raw: unknown,
  options: SplitMultiValueOptions = {},
): string[] {
  if (raw == null || raw === '') return [];
  const s = String(raw).trim();
  if (!s) return [];

  const { knownLabels = [], aliases = {} } = options;
  const aliasKeys = Object.keys(aliases);
  const dictionary = [...knownLabels, ...aliasKeys].sort(
    (a, b) => b.length - a.length,
  );

  if (dictionary.length === 0) {
    return splitMultiValueCell(s);
  }

  const out: string[] = [];
  let i = 0;
  while (i < s.length) {
    while (i < s.length && (isSepChar(s[i]) || /\s/.test(s[i]))) {
      i += 1;
    }
    if (i >= s.length) break;

    let matched: string | null = null;
    for (const label of dictionary) {
      if (!s.startsWith(label, i)) continue;
      const end = i + label.length;
      if (end === s.length || isSepChar(s[end]) || /\s/.test(s[end] ?? '')) {
        matched = label;
        break;
      }
    }

    if (matched) {
      out.push(aliases[matched] ?? matched);
      i += matched.length;
      continue;
    }

    let j = i + 1;
    while (j < s.length && !isSepChar(s[j])) {
      j += 1;
    }
    const piece = s.slice(i, j).trim();
    if (piece) out.push(aliases[piece] ?? piece);
    i = j;
  }

  return out;
}

/** 将已是数组的字段再展平一次（兼容项内仍含顿号拼接的情况） */
export function flattenLabeledMultiValues(
  values: unknown[],
  options: SplitMultiValueOptions = {},
): string[] {
  const out: string[] = [];
  for (const v of values) {
    if (v == null) continue;
    if (typeof v === 'string' && /[、,，;；]/.test(v)) {
      out.push(...splitLabeledMultiValue(v, options));
    } else {
      const t = String(v).trim();
      if (t) out.push(options.aliases?.[t] ?? t);
    }
  }
  return out;
}
